import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, Building2, Clock3, RefreshCw, Users } from 'lucide-react';
import { Button } from '../ui/button';

interface UserUsageAnalyticsTabProps {
  token: string;
}

interface UsageAnalyticsUser {
  key: string;
  userId: string | null;
  email: string | null;
  name: string | null;
  role: string | null;
  isEnabled: boolean;
  createdAt: string | null;
  firstActivityAt: string | null;
  lastActivityAt: string | null;
  lastLoginAt: string | null;
  lastLogoutAt: string | null;
  totalEvents: number;
  loginCount: number;
  logoutCount: number;
  sessionCount: number;
  totalUsageMs: number;
  totalUsageHours: number;
  currentOnlineMs: number;
  currentOnlineHours: number;
  isCurrentlyActive: boolean;
  openSessionStartedAt: string | null;
}

interface UsageAnalyticsClient {
  clientId: string;
  clientName: string;
  totalUsers: number;
  enabledUsers: number;
  disabledUsers: number;
  activeUsers: number;
  onlineUsers: number;
  isCurrentlyOnline: boolean;
  totalEvents: number;
  totalLogins: number;
  totalLogouts: number;
  totalSessions: number;
  totalUsageMs: number;
  totalUsageHours: number;
  currentOnlineMs: number;
  currentOnlineHours: number;
  avgSessionMinutes: number;
  firstActivityAt: string | null;
  lastActivityAt: string | null;
  lastLoginAt: string | null;
  lastLogoutAt: string | null;
  users: UsageAnalyticsUser[];
}

interface UsageAnalyticsSummary {
  clientCount: number;
  totalUsers: number;
  enabledUsers: number;
  disabledUsers: number;
  usersWithActivity: number;
  onlineUsers: number;
  totalEvents: number;
  totalLogins: number;
  totalLogouts: number;
  totalSessions: number;
  totalUsageMs: number;
  totalUsageHours: number;
  currentOnlineMs: number;
  currentOnlineHours: number;
}

interface UsageAnalyticsResponse {
  success: boolean;
  period: {
    days: number;
    startDate: string;
    endDate: string;
    generatedAt: string;
  };
  filters: {
    clientId: string | null;
  };
  summary: UsageAnalyticsSummary;
  clients: UsageAnalyticsClient[];
}

const formatDateTime = (value: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (totalMs: number) => {
  const ms = Number(totalMs || 0);
  if (!Number.isFinite(ms) || ms <= 0) return '0m';

  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const formatMinutes = (totalMs: number) => {
  const ms = Number(totalMs || 0);
  if (!Number.isFinite(ms) || ms <= 0) return '0m';
  return `${Math.floor(ms / 60000)}m`;
};

const formatRole = (role: string | null) => {
  if (!role) return '-';
  return role.replace(/_/g, ' ');
};

export function UserUsageAnalyticsTab({ token }: UserUsageAnalyticsTabProps) {
  const [days, setDays] = useState(30);
  const [selectedClientId, setSelectedClientId] = useState('all');
  const [data, setData] = useState<UsageAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchUsageAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({ days: String(days) });
      const response = await fetch(`/api/admin/user-usage-analytics?${params.toString()}`, { headers });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
      }
      setData(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load usage analytics');
    } finally {
      setLoading(false);
    }
  }, [days, headers]);

  useEffect(() => {
    fetchUsageAnalytics();
  }, [fetchUsageAnalytics]);

  const visibleClients = useMemo(() => {
    const source = data?.clients || [];
    if (selectedClientId === 'all') {
      return source;
    }
    return source.filter((client) => client.clientId === selectedClientId);
  }, [data, selectedClientId]);

  const visibleUsers = useMemo(() => {
    return visibleClients
      .flatMap((client) => client.users.map((user) => ({ ...user, clientName: client.clientName, clientId: client.clientId })))
      .sort((a, b) => {
        const aLast = a.lastActivityAt ? new Date(a.lastActivityAt).getTime() : 0;
        const bLast = b.lastActivityAt ? new Date(b.lastActivityAt).getTime() : 0;
        if (aLast !== bLast) return bLast - aLast;
        return b.totalUsageMs - a.totalUsageMs;
      });
  }, [visibleClients]);

  const summary = data?.summary;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
            <Users className="h-5 w-5 text-indigo-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">User Usage Analytics</h1>
            <p className="text-sm text-muted-foreground">Client-wise users, activity times, and system usage duration</p>
          </div>
        </div>

        <Button variant="outline" size="sm" className="gap-2" onClick={fetchUsageAnalytics}>
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <div className="rounded-xl border border-border bg-card px-3 py-2">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">Period</label>
          <select
            value={String(days)}
            onChange={(event) => setDays(Number.parseInt(event.target.value, 10) || 30)}
            className="ml-3 bg-transparent text-sm text-foreground focus:outline-none"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 180 days</option>
            <option value="365">Last 365 days</option>
          </select>
        </div>

        <div className="rounded-xl border border-border bg-card px-3 py-2">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">Client</label>
          <select
            value={selectedClientId}
            onChange={(event) => setSelectedClientId(event.target.value)}
            className="ml-3 bg-transparent text-sm text-foreground focus:outline-none"
          >
            <option value="all">All Clients</option>
            {(data?.clients || []).map((client) => (
              <option key={client.clientId} value={client.clientId}>{client.clientName}</option>
            ))}
          </select>
        </div>

        <div className="text-xs text-muted-foreground">
          Generated: {formatDateTime(data?.period?.generatedAt || null)}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Clients</div>
          <div className="mt-2 text-2xl font-semibold text-foreground">{summary?.clientCount || 0}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total Users</div>
          <div className="mt-2 text-2xl font-semibold text-foreground">{summary?.totalUsers || 0}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Users Active</div>
          <div className="mt-2 text-2xl font-semibold text-blue-500">{summary?.usersWithActivity || 0}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Users Online</div>
          <div className="mt-2 text-2xl font-semibold text-emerald-500">{summary?.onlineUsers || 0}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Online Time Now</div>
          <div className="mt-2 text-2xl font-semibold text-emerald-500">{formatDuration(summary?.currentOnlineMs || 0)}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Sessions</div>
          <div className="mt-2 text-2xl font-semibold text-foreground">{summary?.totalSessions || 0}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 sm:col-span-2 lg:col-span-1">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Usage Time</div>
          <div className="mt-2 text-2xl font-semibold text-violet-500">{summary?.totalUsageHours || 0}h</div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold text-foreground mb-2">Client Summary</h2>
        <p className="text-sm text-muted-foreground mb-4">Each row shows users under a client and their combined system usage during the selected period.</p>

        <div className="overflow-auto border border-border/70 rounded-lg max-h-[45vh]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/70 backdrop-blur text-left">
              <tr>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Users</th>
                <th className="px-3 py-2">Active</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Logins</th>
                <th className="px-3 py-2">Logouts</th>
                <th className="px-3 py-2">Sessions</th>
                <th className="px-3 py-2">Online Now</th>
                <th className="px-3 py-2">Usage</th>
                <th className="px-3 py-2">Avg Session</th>
                <th className="px-3 py-2">Last Login</th>
                <th className="px-3 py-2">Last Logout</th>
                <th className="px-3 py-2">Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-3 py-6 text-muted-foreground" colSpan={13}>Loading client analytics...</td>
                </tr>
              ) : visibleClients.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-muted-foreground" colSpan={13}>No client usage data found for this period.</td>
                </tr>
              ) : (
                visibleClients.map((client) => (
                  <tr key={client.clientId} className="border-t border-border/60">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-medium text-foreground">{client.clientName}</div>
                      <div className="text-xs text-muted-foreground">{client.clientId}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{client.totalUsers}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{client.activeUsers}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs border ${client.isCurrentlyOnline ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' : 'bg-muted text-muted-foreground border-border'}`}>
                        {client.isCurrentlyOnline ? `Online (${client.onlineUsers})` : 'Offline'}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{client.totalLogins}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{client.totalLogouts}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{client.totalSessions}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDuration(client.currentOnlineMs)} ({formatMinutes(client.currentOnlineMs)})</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDuration(client.totalUsageMs)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{client.avgSessionMinutes}m</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(client.lastLoginAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(client.lastLogoutAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(client.lastActivityAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold text-foreground mb-2">User Details</h2>
        <p className="text-sm text-muted-foreground mb-4">Detailed activity history for users under the currently selected client filter.</p>

        <div className="overflow-auto border border-border/70 rounded-lg max-h-[55vh]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted/70 backdrop-blur text-left">
              <tr>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Online Since</th>
                <th className="px-3 py-2">Online Now</th>
                <th className="px-3 py-2">Last Login</th>
                <th className="px-3 py-2">Last Logout</th>
                <th className="px-3 py-2">Last Active</th>
                <th className="px-3 py-2">Logins</th>
                <th className="px-3 py-2">Logouts</th>
                <th className="px-3 py-2">Sessions</th>
                <th className="px-3 py-2">Usage</th>
                <th className="px-3 py-2">Events</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-3 py-6 text-muted-foreground" colSpan={14}>Loading user analytics...</td>
                </tr>
              ) : visibleUsers.length === 0 ? (
                <tr>
                  <td className="px-3 py-6 text-muted-foreground" colSpan={14}>No user activity found for this filter.</td>
                </tr>
              ) : (
                visibleUsers.map((user) => (
                  <tr key={`${user.clientId}-${user.key}`} className="border-t border-border/60 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-foreground">{user.name || user.email || 'Unknown User'}</div>
                      <div className="text-xs text-muted-foreground">{user.email || user.userId || '-'}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="text-foreground">{user.clientName}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap capitalize">{formatRole(user.role)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs border ${user.isCurrentlyActive ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30' : 'bg-muted text-muted-foreground border-border'}`}>
                        {user.isCurrentlyActive ? 'Online' : user.isEnabled ? 'Offline' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(user.openSessionStartedAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDuration(user.currentOnlineMs)} ({formatMinutes(user.currentOnlineMs)})</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(user.lastLoginAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(user.lastLogoutAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(user.lastActivityAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{user.loginCount}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{user.logoutCount}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{user.sessionCount}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{formatDuration(user.totalUsageMs)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{user.totalEvents}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Activity className="h-3.5 w-3.5" /> Total Events</div>
          <div className="mt-2 text-xl font-semibold text-foreground">{summary?.totalEvents || 0}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Clock3 className="h-3.5 w-3.5" /> Total Logins</div>
          <div className="mt-2 text-xl font-semibold text-foreground">{summary?.totalLogins || 0}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> Enabled Users</div>
          <div className="mt-2 text-xl font-semibold text-foreground">{summary?.enabledUsers || 0}</div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" /> Disabled Users</div>
          <div className="mt-2 text-xl font-semibold text-foreground">{summary?.disabledUsers || 0}</div>
        </div>
      </div>
    </div>
  );
}
